import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Avatar from 'src/components/ui/Avatar/Avatar';
import Button from 'src/components/ui/Button/Button';
import { FormAutocomplete } from 'src/components/ui/FormAutocomplete/FormAutocomplete';
import { FormInput } from 'src/components/ui/FormInput/FormInput';
import { FormPhoneInput } from 'src/components/ui/FormPhoneInput/FormPhoneInput';
import { FormRadio } from 'src/components/ui/FormRadio/FormRadio';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { routes } from 'src/routes';
import {
  addServiceAccount,
  editServiceAccountDetails,
} from 'src/store/actions/serviceAccountActions';
import { Company } from 'src/store/models/Company';
import {
  AccountStatus,
  ServiceAccountAccess,
} from 'src/store/models/ServiceAccount';
import { getCompanyName } from 'src/utils/companyUtils';
import { useErrorSelector } from 'src/utils/useLoadingSelector';

import classes from '../ServiceAccountDetails.module.scss';
import ServiceAccountDetailsFormSkeleton from './ServiceAccountDetailsFormSkeleton';

export interface ServiceAccountDetailsFormData {
  id?: string;
  title: string;
  eMail: string;
  phone: string;
  company: Company;
  access: string;
  enabled: string;
  notes?: string;
}

export interface ServiceAccountDetailsFormProps {
  loading?: boolean;
  edit?: boolean;
}

const ServiceAccountDetailsForm: FC<ServiceAccountDetailsFormProps> = ({
  loading = false,
  edit = false,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { company: Company };

  const serviceAccountDetails = useAppSelector(
    (state) => state.serviceAccount.serviceAccountDetails
  );
  const companyList = useAppSelector((state) => state.company.companyList);

  const createError = useErrorSelector<ServiceAccountDetailsFormData>({
    actionType: edit ? editServiceAccountDetails.type : addServiceAccount.type,
  });

  const { control, watch, getValues, handleSubmit, setValue } =
    useFormContext<ServiceAccountDetailsFormData>();
  const notes = watch('notes');

  useEffect(() => {
    if (companyList?.data && locationState?.company) {
      setValue(
        'company',
        companyList?.data?.find((c) => c.id === locationState.company?.id) ||
          null
      );
    }
  }, [companyList, locationState?.company, setValue]);

  const onSubmit: SubmitHandler<ServiceAccountDetailsFormData> = (formData) => {
    if (edit) {
      dispatch(editServiceAccountDetails(formData));
    } else {
      dispatch(addServiceAccount(formData));
    }
  };

  return (
    <form
      noValidate
      id="service-account-form"
      className={classNames({ [classes.Loading]: loading })}
      onSubmit={handleSubmit(onSubmit)}
    >
      {!loading ? (
        <>
          <h4 className={classNames({ [classes.Edit]: edit })}>
            {t('service_account:technician_info')}
          </h4>
          {edit && (
            <Avatar
              className={classes.Avatar}
              showTitles={false}
              imageSize={96}
              title={getValues()?.title}
              image={serviceAccountDetails?.image}
            />
          )}
          <section>
            <FormInput<ServiceAccountDetailsFormData>
              control={control}
              name="title"
              placeholder={t('common:enter_name')}
              label={t('common:name')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.title}
              helperText={createError?.title}
            />
            <FormInput<ServiceAccountDetailsFormData>
              control={control}
              name="eMail"
              placeholder={t('common:enter_email')}
              label={t('common:email')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.eMail}
              helperText={createError?.eMail}
              disabled={edit}
            />
            <FormPhoneInput<ServiceAccountDetailsFormData>
              control={control}
              name="phone"
              placeholder={t('common:enter_mobile')}
              label={t('common:mobile')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.phone}
              helperText={createError?.phone}
              isMobile
            />
          </section>
          <h4 className={classNames({ [classes.Edit]: edit })}>
            {t('service_account:company_info')}
          </h4>
          <section className={classes.SelectCompany}>
            <FormAutocomplete<ServiceAccountDetailsFormData, Company>
              name="company"
              control={control}
              AutocompleteProps={{
                getOptionLabel: (option) => getCompanyName(option),
                isOptionEqualToValue: (option, value) =>
                  option?.id === value?.id,
                renderOption: (props, option) => {
                  return (
                    <li {...props} key={option.id}>
                      {getCompanyName(option)}
                    </li>
                  );
                },
                options: companyList?.data,
                label: t('common:company'),
                placeholder: `${t('common:choose')}...`,
              }}
              rules={formRules}
              InputProps={{
                inputProps: {
                  autoComplete: 'new-password',
                },
              }}
            />
            <div>
              <p>{t('common:or')?.toLowerCase()}</p>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(routes.NEW_COMPANY)}
              >
                {t('service_account:add_new_company')}
              </Button>
            </div>
          </section>
          <h4>{t('common:access')}</h4>
          <FormRadio<ServiceAccountDetailsFormData>
            name="access"
            control={control}
            rules={formRules}
            radios={[
              {
                value: ServiceAccountAccess.FULL,
                label: `${t(`common:${ServiceAccountAccess.FULL}`)} ${t(
                  'common:access'
                ).toLowerCase()}`,
              },
              {
                value: ServiceAccountAccess.LIMITED,
                label: `${t(`common:${ServiceAccountAccess.LIMITED}`)} ${t(
                  'common:access'
                ).toLowerCase()}`,
              },
            ]}
            LabelRadioProps={{ color: 'secondary', className: classes.Radio }}
          />
          <p>{t('service_account:full_access_information')}</p>
          {edit && (
            <>
              <h4>{t('service_account:account_status')}</h4>
              <FormRadio<ServiceAccountDetailsFormData>
                name="enabled"
                control={control}
                rules={formRules}
                radios={[
                  {
                    value: AccountStatus.ACTIVATED,
                    label: t(`common:${AccountStatus.ACTIVATED}`),
                  },
                  {
                    value: AccountStatus.DEACTIVATED,
                    label: t(`common:${AccountStatus.DEACTIVATED}`),
                  },
                ]}
                LabelRadioProps={{
                  color: 'secondary',
                  className: classes.Radio,
                }}
              />
              <p>{t('service_account:account_status_details')}</p>
            </>
          )}
          <h4>{t('service_account:other_information')}</h4>
          <FormInput<ServiceAccountDetailsFormData>
            control={control}
            name="notes"
            placeholder={t('service_account:add_notes')}
            label={`${t('common:notes')} (${t(
              'common:optional'
            ).toLowerCase()})`}
            inputProps={{
              fullWidth: true,
              multiline: true,
              rows: 5,
              inputProps: { maxLength: 500 },
            }}
            className={classes.Notes}
            error={!!createError?.notes}
            helperText={`${notes?.length || 0}/500`}
          />
        </>
      ) : (
        <ServiceAccountDetailsFormSkeleton />
      )}
    </form>
  );
};

export default ServiceAccountDetailsForm;
